import React from 'react';
import dayjs from 'dayjs';
import s from './s.module.less';
import WeekDay from '../WeekDay';
import { getDateKey, getEventColorSetting, isCancelledEvent } from 'utils/calendar';
import { Event } from 'types/calendar';
import { Popover, Modal, notification } from 'antd';
import AddAndEditCalendarEvent from 'components/AddAndEditCalendarEvent';
import { KlarityService, Prospect } from 'types/common';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    calendarCreateOrUpdateAppointment,
    calendarCreateOrUpdateBlocker,
    getCalendarAppointmentEventDetail,
    getCalendarBlockerEventDetail,
} from 'api/google';
import { getUserId } from 'utils/localstore';
import useCurrentUser from 'hooks/useCurrentUser';

type Props = {
    weekDaysArray: dayjs.Dayjs[];
    dateEventMap: Record<string, Event[]>;
    onEventEdit?: (event: Event) => void;
    onEventAdd?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void;
    pendingStartTime?: dayjs.Dayjs;
    pendingEndTime?: dayjs.Dayjs;
    timezone?: string;
    onHidePopLayer?: () => void;
    onSuccessAddNew?: () => void;
    allSpecialityData?: KlarityService[];
    allClients?: Prospect[];
    providerColorMap?: Record<number, string>;
    targetUrlEventId?: string;
    onEditRefillTask?: (item: any) => void;
    isGroupPractice?: boolean;
};

const formatHourLabel = (hour24: number) => {
    const h = ((hour24 % 24) + 24) % 24;
    const isAm = h < 12;
    if (h === 0) return '12 am';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12} ${isAm ? 'am' : 'pm'}`;
};

const Week = (props: Props) => {
    const {
        weekDaysArray,
        dateEventMap,
        onEventEdit,
        onEventAdd,
        pendingStartTime,
        pendingEndTime,
        timezone,
        onHidePopLayer,
        onSuccessAddNew,
        allSpecialityData,
        allClients,
        providerColorMap,
        targetUrlEventId,
        onEditRefillTask,
        isGroupPractice,
    } = props;
    const [currentUser] = useCurrentUser();
    const { startHour, endHour } = React.useMemo(() => {
        let latest: number | undefined;

        weekDaysArray.forEach((d) => {
            const key = getDateKey(d);
            const events = dateEventMap[key] || [];
            events.forEach((evt) => {
                if (evt.type === 'task') return;
                let endTime = dayjs(evt.end?.dateTime);
                if (timezone) {
                    endTime = endTime.tz(timezone);
                }
                const eh = endTime.hour();
                latest = latest === undefined ? eh : Math.max(latest, eh);
            });
        });

        const start = 0;
        const endByLatest = latest === undefined ? 24 : Math.max(24, latest + 1);

        return { startHour: start, endHour: endByLatest };
    }, [dateEventMap, timezone, weekDaysArray]);

    const timeArray = React.useMemo(() => {
        const hours: number[] = [];
        for (let h = startHour; h <= endHour; h++) hours.push(h);
        return hours.map(formatHourLabel);
    }, [startHour, endHour]);

    const weekDayHeight = React.useMemo(() => {
        const rows = Math.max(1, endHour - startHour + 1);
        return 140 + rows * 50 + 20;
    }, [startHour, endHour]);

    const getPendingTop = React.useCallback(
        (start: dayjs.Dayjs) => {
            // Align with the grid math:
            // - 50px per hour (same as WeekDay)
            // - plus the header offset used in the click handler (146px)
            const minutesFromViewStart = Math.max(0, (start.hour() - startHour) * 60 + start.minute());
            return 146 + (minutesFromViewStart * 50) / 60;
        },
        [startHour],
    );

    const [currentFocusDayStr, setCurrentFocusDayStr] = React.useState<string>();
    const [activeDragEvent, setActiveDragEvent] = React.useState<Event | null>(null);
    const getToastContainer = React.useCallback(() => {
        return document.getElementById('calendarWrap') || document.body;
    }, []);
    // Place notification container inside calendar so we can position it bottom-center via CSS.
    const [toastApi, toastContextHolder] = notification.useNotification({ getContainer: getToastContainer });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 4 },
        }),
    );

    const roundTo15Min = React.useCallback((mins: number) => Math.round(mins / 15) * 15, []);

    const openConfirmAndPersist = React.useCallback(
        async (event: Event, nextStart: dayjs.Dayjs, nextEnd: dayjs.Dayjs) => {
            let start = dayjs(event.start?.dateTime);
            let end = dayjs(event.end?.dateTime);
            if (timezone) {
                start = start.tz(timezone);
                end = end.tz(timezone);
            }

            const oldLabel = `${start.format('MMM D, YYYY')} ${start.format('h:mm A')} – ${end.format('h:mm A')}`;
            const newLabel = `${nextStart.format('MMM D, YYYY')} ${nextStart.format('h:mm A')} – ${nextEnd.format(
                'h:mm A',
            )}`;

            const modal = Modal.confirm({
                title: 'Confirm time change',
                okText: 'Confirm',
                cancelText: 'Cancel',
                content: (
                    <div className={s.dragConfirm}>
                        <div className={s.dragConfirmTitle}>You’re about to change this event time.</div>
                        <div className={s.dragConfirmMeta}>
                            <div>From: {oldLabel}</div>
                            <div>To: {newLabel}</div>
                        </div>
                    </div>
                ),
                onOk: () => {
                    // Always close immediately, even if the API fails (avoid sticky confirm modal).
                    modal.destroy();

                    (async () => {
                        try {
                            const startAtUtc = nextStart.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
                            const endAtUtc = nextEnd.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

                            if (event.type === 'blocker') {
                                const detailRes = await getCalendarBlockerEventDetail(`${event.id}`);
                                if (detailRes.error) throw new Error('Failed to load event details');
                                const detail = detailRes.data?.data;
                                if (!detail) throw new Error('Failed to load event details');
                                const payload = { ...detail, startAt: startAtUtc, endAt: endAtUtc };
                                const saveRes = await calendarCreateOrUpdateBlocker(payload);
                                if (saveRes.error) throw new Error('Failed to update event');
                            } else {
                                const detailRes = await getCalendarAppointmentEventDetail(`${event.id}`);
                                if (detailRes.error) throw new Error('Failed to load event details');
                                const detail = detailRes.data?.data;
                                if (!detail) throw new Error('Failed to load event details');
                                const payload = { ...detail, startAt: startAtUtc, endAt: endAtUtc };
                                const gpOverrideProviderId = isGroupPractice ? detail?.providerId : undefined;
                                const saveRes = await calendarCreateOrUpdateAppointment(payload, gpOverrideProviderId);
                                if (saveRes.error) throw new Error('Failed to update event');
                            }

                            toastApi.open({
                                className: s.dragSuccessToast,
                                message: <span className={s.dragSuccessTitle}>Visit time updated successfully</span>,
                                closeIcon: <span className={s.dragSuccessClose} aria-hidden />,
                                icon: null,
                                description: null,
                                placement: 'bottomRight',
                                duration: 2,
                            });
                            onSuccessAddNew?.();
                        } catch {
                            toastApi.open({
                                message: 'Failed to update visit time',
                                placement: 'topRight',
                                duration: 2,
                            });
                        } finally {
                            // Safety: make sure no confirm modal is left open.
                            Modal.destroyAll();
                        }
                    })();
                },
                onCancel: () => {},
            });
        },
        [onSuccessAddNew, timezone, toastApi],
    );

    const getOverlayThemeClassName = React.useCallback(
        (evt: Event) => {
            const css = getEventColorSetting(evt, allClients, providerColorMap);
            const bg = (css?.backgroundColor || '').toUpperCase();
            const borderStyle = css?.borderStyle || 'solid';
            const isBlocker = evt.type === 'blocker';
            const isCancelled = isCancelledEvent(evt);

            const parts: string[] = [s.dragOverlayCard];
            if (isBlocker) parts.push(s.dragOverlayBlocker);
            if (isCancelled) parts.push(s.dragOverlayCancelled);
            if (borderStyle === 'dashed') parts.push(s.dragOverlayDashed);
            if (borderStyle === 'solid') parts.push(s.dragOverlaySolid);

            // Map known themes (from `getEventColorSetting`) to CSS classes.
            switch (bg) {
                case '#1D86DB':
                    parts.push(s.themeBlueStrong);
                    break;
                case '#D3EBFF':
                case '#F1F9FF':
                    parts.push(s.themeBlueSoft);
                    break;
                case '#009572':
                    parts.push(s.themeGreenStrong);
                    break;
                case '#F0FDFA':
                    parts.push(s.themeGreenSoft);
                    break;
                case '#C4B5FD':
                case '#F5F3FF':
                    parts.push(s.themePurple);
                    break;
                case '#F4EA56':
                case '#FFFBBF':
                    parts.push(s.themeYellow);
                    break;
                case '#333D96':
                case '#CFDFEE':
                    parts.push(s.themeIndigo);
                    break;
                case '#FBBF24':
                case '#FCFFEC':
                    parts.push(s.themeAmber);
                    break;
                case '#FCA5A5':
                case '#FFF6FB':
                    parts.push(s.themeRed);
                    break;
                case '#6AFFFF':
                case '#CAFFFF':
                    parts.push(s.themeCyan);
                    break;
                default:
                    parts.push(s.themeBlueSoft);
                    break;
            }
            return parts.join(' ');
        },
        [allClients],
    );

    const getOverlayTimeLabel = React.useCallback(
        (evt: Event) => {
            let start = dayjs(evt.start?.dateTime);
            let end = dayjs(evt.end?.dateTime);
            if (timezone) {
                start = start.tz(timezone);
                end = end.tz(timezone);
            }
            return `${start.format('hh:mm')} - ${end.format('hh:mm A')}`;
        },
        [timezone],
    );

    const handleDragStart = React.useCallback(
        (evt: DragStartEvent) => {
            const e = evt.active?.data?.current?.event as Event | undefined;
            if (!e) return;
            if (e.type === 'google') {
                toastApi.open({
                    message: "Drag & drop isn't supported for Google events.",
                    placement: 'topRight',
                    duration: 2,
                });
                return;
            }
            setActiveDragEvent(e);
        },
        [toastApi],
    );

    const handleDragCancel = React.useCallback(() => {
        setActiveDragEvent(null);
    }, []);

    const handleDragEnd = React.useCallback(
        async (evt: DragEndEvent) => {
            setActiveDragEvent(null);
            const dragged = evt.active?.data?.current;
            const over = evt.over?.data?.current;
            const event = dragged?.event as Event | undefined;
            const fromDayKey = dragged?.dayKey as string | undefined;
            const toDayKey = (over?.dayKey as string | undefined) || fromDayKey;

            if (!event || !fromDayKey || !toDayKey) return;
            if (event.type === 'task') return;
            if (event.type === 'google') return;
            const eventProviderId = event?.providerId;
            const currentActingProvider = currentUser?.provider?.id;
            if (`${currentActingProvider}` !== `${eventProviderId}`) return;

            if (Number.isNaN(Number.parseFloat(`${evt.delta?.y ?? 0}`))) return;

            const fromDay = weekDaysArray.find((d) => getDateKey(d) === fromDayKey);
            const toDay = weekDaysArray.find((d) => getDateKey(d) === toDayKey);
            if (!fromDay || !toDay) return;

            // Derive current times in display timezone (same as positioning logic in `WeekDay`).
            let start = dayjs(event.start?.dateTime);
            let end = dayjs(event.end?.dateTime);
            if (timezone) {
                start = start.tz(timezone);
                end = end.tz(timezone);
            }

            // If cross-day event, skip drag-reschedule to avoid accidental truncation.
            if (!start.isSame(end, 'day')) return;

            const durationMins = Math.max(1, end.diff(start, 'minute'));

            // Pixel->minute: existing layout is 50px per hour => 1px = 1.2 minutes.
            const deltaMinutesRaw = (evt.delta?.y || 0) * (6 / 5);
            const deltaMinutes = roundTo15Min(deltaMinutesRaw);
            const dayShift = toDay.startOf('day').diff(fromDay.startOf('day'), 'day');

            let nextStart = start.add(dayShift, 'day').add(deltaMinutes, 'minute');
            let nextEnd = nextStart.add(durationMins, 'minute');

            const dayStart = toDay.startOf('day');
            const dayEnd = toDay.endOf('day');
            if (nextStart.isBefore(dayStart)) {
                nextStart = dayStart;
                nextEnd = nextStart.add(durationMins, 'minute');
            }
            if (nextEnd.isAfter(dayEnd)) {
                nextEnd = dayEnd;
                nextStart = nextEnd.add(-durationMins, 'minute');
            }

            // No actual change.
            if (nextStart.isSame(start) && nextEnd.isSame(end)) return;

            openConfirmAndPersist(event, nextStart, nextEnd);
        },
        [openConfirmAndPersist, roundTo15Min, timezone, weekDaysArray],
    );

    const pendingAreaContent = () => {
        return (
            <div className={s.areaWrap}>
                <AddAndEditCalendarEvent
                    onCancel={() => {
                        onHidePopLayer?.();
                    }}
                    onSuccess={() => {
                        onSuccessAddNew?.();
                    }}
                    defaultStart={pendingStartTime}
                    defaultEnd={pendingEndTime}
                    timezone={timezone}
                    isPopLayer
                    allSpecialityData={allSpecialityData}
                />
                <div
                    onClick={() => {
                        onHidePopLayer?.();
                    }}
                    className={s.closeIcon}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
        >
            {toastContextHolder}
            <div className={s.wrap}>
                {/* Full-width sticky white underlay for the week header area (prevents grid/events bleeding through). */}
                <div className={s.weekHeaderUnderlay} aria-hidden />
                <div className={s.weekBg}>
                    <div className={s.mask} />
                    {timeArray.map((item) => {
                        return (
                            <div className={`${s.rowLine} rowLine`} key={item}>
                                <span className={s.label}>{item}</span>
                                <hr className={s.line} />
                            </div>
                        );
                    })}
                </div>
                <div className={s.contentWrap}>
                    {weekDaysArray.map((day, i) => {
                        const key = getDateKey(day);
                        const narrowLayout = false;
                        const showPendingArea = pendingStartTime && getDateKey(day) === getDateKey(pendingStartTime);
                        return (
                            <div onClick={() => {}} className={s.weekDay} style={{ height: weekDayHeight }} key={i}>
                                <div
                                    onClick={(e) => {
                                        const offsetTop = e.nativeEvent.offsetY - 146;
                                        const targetHour = startHour + Math.floor(offsetTop / 50);
                                        const left = offsetTop % 50;
                                        let min = 0;
                                        if (left > 12.5 && left <= 25) {
                                            min = 15;
                                        }
                                        if (left > 25 && left <= 37.5) {
                                            min = 30;
                                        }
                                        if (left > 37.5 && left <= 50) {
                                            min = 45;
                                        }
                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                        const startTime = day.hour(targetHour).minute(min);
                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                        let endTime = day.hour(targetHour).minute(min).add(30, 'minute');
                                        if (endTime.isAfter(dayjs(day).endOf('day'))) {
                                            endTime = dayjs(day).endOf('day');
                                        }
                                        onEventAdd && onEventAdd(startTime, endTime);
                                        setCurrentFocusDayStr(undefined);
                                    }}
                                    className={s.timeCatch}
                                />
                                <WeekDay
                                    droppableDayKey={key}
                                    targetUrlEventId={targetUrlEventId}
                                    currentEditEventDayString={currentFocusDayStr}
                                    day={day}
                                    key={i}
                                    inx={i}
                                    viewStartHour={startHour}
                                    onEventEdit={onEventEdit}
                                    events={dateEventMap[key] || []}
                                    timezone={timezone}
                                    onDayItemClick={(dayStr) => {
                                        onHidePopLayer?.();
                                        setCurrentFocusDayStr(dayStr);
                                    }}
                                    onEventUpdate={() => {
                                        onSuccessAddNew?.();
                                    }}
                                    onEditRefillTask={onEditRefillTask}
                                    allSpecialityData={allSpecialityData}
                                    allClients={allClients}
                                    providerColorMap={providerColorMap}
                                />
                                {showPendingArea && (
                                    <Popover
                                        open
                                        placement="right"
                                        zIndex={1000}
                                        title={null}
                                        content={pendingAreaContent()}
                                        arrow
                                    >
                                        <div
                                            className={`${narrowLayout ? `${s.event} ${s.narrowEvent}` : s.event} ${
                                                s.pendingEvent
                                            }`}
                                            style={{
                                                top: pendingStartTime ? getPendingTop(pendingStartTime) : undefined,
                                            }}
                                            onClick={() => {}}
                                        >
                                            <p className={s.title}>{/* No title */}</p>
                                        </div>
                                    </Popover>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <DragOverlay>
                {activeDragEvent ? (
                    <div className={getOverlayThemeClassName(activeDragEvent)}>
                        <div className={s.dragOverlayTitle}>{activeDragEvent.summary || 'Event'}</div>
                        <div className={s.dragOverlayTime}>{getOverlayTimeLabel(activeDragEvent)}</div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default Week;
